import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Ministry } from '../../../models/ministry/ministry';
import { ROUTES } from '../../../constants/routes';
import MinistryCard from './MinistryCard';

interface MinistriesSectionProps {
  title: string;
  description: string;
  ministries: Ministry[];
  showViewAllButton?: boolean;
  viewAllButtonText?: string;
  viewAllButtonLink?: string;
  limit?: number;
  loading?: boolean;
}

const MinistriesSection: React.FC<MinistriesSectionProps> = ({
  title,
  description,
  ministries,
  showViewAllButton = true,
  viewAllButtonText,
  viewAllButtonLink = ROUTES.MINISTRIES,
  limit,
  loading = false
}) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  // Apply limit if specified
  const displayedMinistries = limit ? ministries.slice(0, limit) : ministries;

  // Loading skeleton
  if (loading) {
    return (
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          {/* Header skeleton */}
          <div className="text-center mb-16">
            <div className="w-16 h-16 bg-gray-200 animate-pulse rounded-full mx-auto mb-6"></div>
            <div className="h-12 bg-gray-200 animate-pulse rounded mx-auto mb-4 max-w-md"></div>
            <div className="h-6 bg-gray-200 animate-pulse rounded mx-auto max-w-2xl"></div>
          </div>

          {/* Cards skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white rounded-2xl shadow-lg overflow-hidden animate-pulse">
                <div className="h-64 bg-gray-200"></div>
                <div className="p-6">
                  <div className="h-6 bg-gray-200 rounded mb-4"></div>
                  <div className="h-4 bg-gray-200 rounded mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded mb-4 w-3/4"></div>
                  <div className="h-10 bg-gray-200 rounded"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white text-black rounded-full mb-6">
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          </div>

          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            {title}
          </h2>

          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            {description}
          </p>
        </div>

        {/* Ministries Grid */}
        {displayedMinistries.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {displayedMinistries.map((ministry) => (
              <MinistryCard key={ministry.id} ministry={ministry} />
            ))}
          </div>
        ) : (
          /* Empty State */
          <div className="text-center py-16">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 text-gray-400 rounded-full mb-6">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-100 mb-2">
              {t('pages.ministries.noMinistries.title')}
            </h3>
            <p className="text-gray-300">
              {t('pages.ministries.noMinistries.message')}
            </p>
          </div>
        )}

        {/* View All Button */}
        {showViewAllButton && displayedMinistries.length > 0 && (
          <div className="text-center">
            <Link
              to={viewAllButtonLink}
              className="inline-flex items-center justify-center bg-white text-black hover:bg-gray-100 px-8 py-4 rounded-lg transition-all duration-300 font-semibold hover:scale-105 shadow-lg hover:shadow-xl"
            >
              <span>{viewAllButtonText || t('pages.home.ministries.readyToBegin.button')}</span>
              <svg className={`w-5 h-5 ${isRTL ? 'mr-2' : 'ml-2'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isRTL ? "M15 19l-7-7 7-7" : "M9 5l7 7-7 7"} />
              </svg>
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};

export default MinistriesSection;
